import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { useAuthStore } from './stores/auth.store'
import { LoginPage } from './routes/auth/LoginPage'
import { AppShell } from './components/layout/AppShell'
import { HomePage } from './routes/HomePage'
import { ObjectWorkList } from './routes/object/ObjectWorkList'
import { ThingInspector } from './routes/object/ThingInspector'
import { SettingsLayout } from './routes/settings/SettingsLayout'
import { ObjectManagement } from './routes/settings/ObjectManagement'
import { UserManagement } from './routes/settings/UserManagement'
import { RoleManagement } from './routes/settings/RoleManagement'
import { ApiTokenManagement } from './routes/settings/ApiTokenManagement'
import { SystemSettings } from './routes/settings/SystemSettings'
import { DataWorkbench } from './routes/settings/DataWorkbench'
import { DeletedData } from './routes/settings/DeletedData'
import { LoginLogs } from './routes/settings/LoginLogs'
import { MashupManagement } from './routes/settings/MashupManagement'
import { AttachmentManagement } from './routes/settings/AttachmentManagement'
import { CustomActionManagement } from './routes/settings/CustomActionManagement'
import { ActionLogs } from './routes/settings/ActionLogs'
import { useEffect } from 'react'
import { fetchCurrentUser } from './api/client'
import { ChangePasswordPage } from './routes/auth/ChangePasswordPage'
import { ErrorBoundary } from './components/shared/ErrorBoundary'
import { ErrorPage } from './routes/ErrorPage'
import { HelpPage } from './routes/HelpPage'
import { ApiDocsPage } from './routes/ApiDocsPage'

function AuthGuard() {
  const { token, user, updateUser, clearAuth } = useAuthStore()
  const location = useLocation()

  useEffect(() => {
    if (token && !user?.permission_summary) {
      fetchCurrentUser()
        .then((res) => updateUser(res.data))
        .catch(() => clearAuth())
    }
  }, [clearAuth, token, updateUser, user?.permission_summary])

  if (!token) {
    return <Navigate to="/login" replace />
  }

  if (user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  if (!user?.must_change_password && location.pathname === '/change-password') {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}

export function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<AuthGuard />}>
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route element={<AppShell />}>
            <Route index element={<HomePage />} />
            <Route path="docs/help" element={<HelpPage />} />
            <Route path="o/:objectId" element={<ObjectWorkList />} />
            <Route path="o/:objectId/:recordId" element={<ThingInspector />} />
            <Route path="settings" element={<SettingsLayout />}>
              <Route index element={<Navigate to="/settings/system" replace />} />
              <Route path="system" element={<SystemSettings />} />
              <Route path="objects" element={<ObjectManagement />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="roles" element={<RoleManagement />} />
              <Route path="api-tokens" element={<ApiTokenManagement />} />
              <Route path="mashups" element={<MashupManagement />} />
              <Route path="custom-actions" element={<CustomActionManagement />} />
              <Route path="data-workbench" element={<DataWorkbench />} />
              <Route path="deleted-data" element={<DeletedData />} />
              <Route path="login-logs" element={<LoginLogs />} />
              <Route path="action-logs" element={<ActionLogs />} />
              <Route path="attachments" element={<AttachmentManagement />} />
            </Route>
          </Route>
        </Route>
        <Route path="/403" element={<ErrorPage code={403} />} />
        <Route path="/500" element={<ErrorPage code={500} />} />
        <Route path="/docs/api" element={<ApiDocsPage />} />
        <Route path="*" element={<ErrorPage code={404} />} />
      </Routes>
    </ErrorBoundary>
  )
}
